// MA-544 — retire the teacher-family add-kid workaround once MA-288's
// kiosk roster door is live for a class-owning teacher account.
//
// TODAY (pre-slice), a teacher-role account can still onboard classroom
// kids as ordinary family kids under their own account — the pre-MA-288
// interim path, consent-corrected by MA-464. Once MA-288's roster door is
// live, this path stops being a benign fallback and becomes an IDENTITY
// TRAP (docs/build/BACKLOG.md's MA-544 item text): a kid added BOTH ways
// exists twice — once as a teacher-family `students` row, once as a
// `class_enrollments` shadow/claimed identity — practice forks across the
// two, and a later family claim can only ever inherit ONE half.
//
// ONE pure predicate + ONE async resolver here, imported by EVERY gated
// entry point (App.jsx's WhosPlayingScreen add-tile, SettingsKids' "+Add a
// kid" affordance, onboarding.kid.handoff.tsx's commit step) — a single
// source of truth so the three call sites can never drift out of sync the
// way three independently-written `if` statements eventually would.
//
// MA-557 (MA-DEC-0824 D1/D2, closing the P3 bypass docs/build/specs/MA-556-
// role-model-review.md names): this gate used to check `getUserRole()`
// FIRST and hard-short-circuit to "never blocked" for any device not
// CURRENTLY flipped to "teacher" — so a class-owning account could unblock
// itself just by flipping its OWN device to "parent" mode; the account fact
// (does it own a class?) never even got read. The gate now keys ONLY on the
// ACCOUNT fact — class ownership — so the device's role string cannot change
// the answer for a class-owning account either way. A brand-new teacher who
// owns zero classes yet is still unblocked (unchanged from MA-544 — see
// fetchTeacherClassCountForGate's own header, also unchanged: it keeps its
// own fail-OPEN-to-0 error handling, a deliberate MA-544 bootstrapping
// exception to the D4 fail-stricter default elsewhere in this slice).
import { useEffect, useState } from "react";
import { fetchTeacherClasses, type SupabaseClient } from "@/lib/teacher/classApi";
import { supabase } from "@/supabaseClient.js";

/** Verbatim from the MA-544 item text — the ONE place this literal string
 *  is written, so every gated surface renders the exact same honest
 *  copy. */
export const TEACHER_ADD_KID_REDIRECT_COPY =
  "Add students to your class roster — they practice from the class device door; families link from home.";

/**
 * PURE core. Keys on the ACCOUNT fact alone — `ownsClass`, a boolean derived
 * server-side (fetchTeacherClassCountForGate below) — never on any
 * device-local role string. `true` blocks family-add; a non-`true`
 * `ownsClass` (garbage/stale/absent) never blocks, matching D4's stricter-
 * lane-on-uncertainty posture translated into this gate's terms (uncertain
 * ownership must not brick an unrelated write path).
 */
export function blocksFamilyAddKid(ownsClass: boolean): boolean {
  return ownsClass === true;
}

/**
 * Resolves how many classes this signed-in account owns, fail-OPEN to 0 on
 * ANY error — a missing-table pre-apply state, an RLS hiccup, a network
 * blip, anything. Per the MA-544 acceptance: "a teacher-role account with
 * NO classes yet (brand-new teacher mid-setup) still can [add a family
 * kid] (never brick onboarding)" — a failed read must never LOOK like
 * "owns a class" and strand a brand-new teacher who has nowhere else to
 * add their first kid yet. `client` is the same test-only DI seam
 * classApi.ts's own functions use; production callers never pass it.
 */
export async function fetchTeacherClassCountForGate(
  teacherId: string,
  client: SupabaseClient = supabase,
): Promise<number> {
  try {
    const classes = await fetchTeacherClasses(teacherId, client);
    return classes.length;
  } catch {
    return 0;
  }
}

/**
 * The one async call every gated entry point makes. Always issues the
 * classes read (once a signed-in account id exists) regardless of the
 * device's role string — THE BYPASS this slice closes was precisely a
 * short-circuit that skipped this fetch whenever the device wasn't
 * currently flipped to "teacher". No account id yet (nothing signed in) is
 * the only case that still short-circuits, since there is no account to
 * check ownership for.
 */
export async function resolveFamilyAddKidGate(
  teacherId: string | null,
  client: SupabaseClient = supabase,
): Promise<boolean> {
  if (!teacherId) return false;
  const classCount = await fetchTeacherClassCountForGate(teacherId, client);
  return blocksFamilyAddKid(classCount > 0);
}

/**
 * React seam shared by every gated surface (App.jsx, SettingsKids,
 * onboarding.kid.handoff.tsx). Starts at `false` (never block) rather than
 * an undefined/loading tri-state: every gated affordance here is an
 * ADDITIVE UI element (a tile, a button, a form) that a teacher would tap
 * INTENTIONALLY, never a destructive action fired implicitly — so a brief
 * unblocked flash while the class-count fetch resolves is the safe
 * default. It is not this hook's only line of defense either: every
 * surface ALSO guards its own underlying write/commit function
 * (`onAddKid`, `handoff`) against `blocked`, so even a race in this exact
 * window still cannot complete a family-style kid creation for a
 * class-owning teacher — see this repo's MA-544 report for the "entry
 * affordance + underlying flow entry" defense-in-depth this hook is one
 * half of.
 */
export function useFamilyAddKidGateBlocked(teacherId: string | null): boolean {
  const [blocked, setBlocked] = useState(false);
  useEffect(() => {
    let alive = true;
    resolveFamilyAddKidGate(teacherId).then((result) => {
      if (alive) setBlocked(result);
    });
    return () => {
      alive = false;
    };
  }, [teacherId]);
  return blocked;
}

/**
 * MA-CLOSURE6 (closure-instrument slice 6) — ADDITIVE sibling of
 * `useFamilyAddKidGateBlocked` above, which that hook's own header already
 * disclosed does not distinguish "resolved to unblocked" from "the classes
 * fetch just hasn't resolved yet" (both render `false`). The two existing
 * gated surfaces (App.jsx's WhosPlayingScreen tile, onboarding.kid.handoff.tsx)
 * still use the byte-unchanged `useFamilyAddKidGateBlocked` above — this
 * variant exists ONLY for ParentSettingsSections.tsx's SettingsKids, which
 * needs the resolved/unresolved distinction to render an honest live-QA
 * DOM marker (never to change what a parent/teacher sees — see that call
 * site's own `add-kid-gate-resolving` hidden marker). Same fail-toward-
 * unblocked default (`{blocked: false, resolved: false}` on first render,
 * on teacherId change, and while the fetch is in flight) as the sibling
 * hook — this is additive instrumentation, not a stricter gate.
 */
export function useFamilyAddKidGateStatus(teacherId: string | null): { blocked: boolean; resolved: boolean } {
  const [state, setState] = useState<{ blocked: boolean; resolved: boolean }>({ blocked: false, resolved: false });
  useEffect(() => {
    let alive = true;
    setState({ blocked: false, resolved: false });
    resolveFamilyAddKidGate(teacherId).then((result) => {
      if (alive) setState({ blocked: result, resolved: true });
    });
    return () => {
      alive = false;
    };
  }, [teacherId]);
  return state;
}
